import { useState, useCallback } from "react";
import Cron, { type CronError } from "react-js-cron";
import "react-js-cron/dist/styles.css";

import type { ISchedule } from "Pages/Setup/Scheduler/Scheduler";
import styles from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/CronSelector.module.css";
import {
    Button,
    Col,
    Form,
    FormFeedback,
    Input,
    Label,
    Row,
    UncontrolledTooltip,
} from "reactstrap";
import IntegrationOption from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/IntegrationOption/IntegrationOption";
import cronstrue from "cronstrue";
import IntegrationArguments from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/IntegrationArguments/IntegrationArguments";
import RateSelector from "Pages/Setup/Scheduler/Components/AddSchedule/CronSelector/RateSelector/RateSelector";
import {
    DragDropContext,
    Droppable,
    Draggable,
    type DropResult,
} from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import {
    joinScheduleIntegration,
    splitScheduleIntegration,
} from "Pages/Setup/Scheduler/scheduleArgs";

interface IProps {
    checkIfNameValid?: (name: string) => void;
    integrations: IIntegration[];
    nameValid?: boolean | undefined;
    onSetSchedule: (schedule: ISchedule) => void;
    parent?: string;
    schedule: ISchedule;
    integrationInputs: number;
    onSetIntegrationInputs: (integrationInputs: number) => void;
    isRateInvalid: boolean;
    onSetIsRateInvalid: (isInvalid: boolean) => void;
}

const defaultCron = "0 * * * *";

/** Uses a component from an NPM package to provide a UI to generate cron expressions.
 * @link https://xrutayisire.github.io/react-js-cron/?path=/docs/reactjs-cron--demo */
export default function CronSelector({
    checkIfNameValid,
    integrations,
    nameValid,
    onSetSchedule,
    parent,
    schedule,
    integrationInputs,
    onSetIntegrationInputs,
    isRateInvalid,
    onSetIsRateInvalid,
}: IProps) {
    /** Error message the comes from the Cron component*/
    const [error, setError] = useState<CronError>();
    const [isRate, setIsRate] = useState<boolean>(
        schedule.trigger.startsWith("rate")
    );

    /** A cron expression. Gets passed to the Cron component */
    const [cron, setCron] = useState<string>(getDefaultCronTrigger());

    function getDefaultCronTrigger() {
        let trigger = "";

        if (
            parent === "edit" &&
            !schedule.trigger.includes(";") &&
            !schedule.trigger.startsWith("rate")
        ) {
            trigger = schedule.trigger
                .split(" ")
                .map((v) => (v.includes("#") ? "*" : v))
                .join(" ");
        } else {
            trigger = defaultCron;
        }

        return trigger;
    }

    /** Callback passed into the Cron react-js-cron component from NPM. Also updates schedule
     * to link the cron value to the schedule state.
     */
    const onSetCron = useCallback(
        (value: string) => {
            setCron(value);
            onSetSchedule({
                ...schedule,
                trigger: value,
            });
        },
        [onSetSchedule, schedule]
    );

    /** Updates schedule state when inputs change */
    const scheduleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        // Integration name input
        if (name === "name") {
            if ((parent === "add" || parent === "edit") && checkIfNameValid) {
                checkIfNameValid(value.trim().replaceAll(" ", "_"));
            }

            onSetSchedule({
                ...schedule,
                name: value,
            });

            // Checkbox input to enable/disable the schedule
        } else if (name === "enabled") {
            onSetSchedule({
                ...schedule,
                enabled: e.target.checked,
            });
        }

        // One of the select option integration dropdown inputs
        else if (name.startsWith("integration")) {
            /** A number (max of 25) index is appended to each input's name after a pipe symbol: "|" */
            const editingIndex = Number.parseInt(name.split("|")[1]);
            const newIntegrations = [...schedule.integrations].map(
                (i, mappingIndex) => {
                    console.log(
                        schedule.integrations.length,
                        editingIndex,
                        mappingIndex
                    );
                    if (mappingIndex === editingIndex) {
                        i = value;
                    }
                    return i;
                }
            );

            onSetSchedule({
                ...schedule,
                integrations: [...newIntegrations],
            });
        } else if (name.startsWith("argument")) {
            // Get indexes from input name
            const inputNameParts = name.split("-");
            const integrationIndex = Number.parseInt(inputNameParts[1]);
            const argumentIndex = Number.parseInt(inputNameParts[2]);

            // Find relevant integration and split it into its name and decoded args.
            // dimension-fhmsz: args are URL-encoded in the stored string, so a '/' the user types
            // stays inside one argument instead of becoming a segment boundary.
            const integration = schedule.integrations[integrationIndex];
            const { process, args } = splitScheduleIntegration(integration);

            // Update the relevant argument.
            args[argumentIndex] = value;

            // If the updated argument is empty, clear out all subsequent arguments
            if (value === "") {
                for (let i = argumentIndex + 1; i < args.length; i++) {
                    args[i] = "";
                }
            }

            // Join back into a string like "someintegration/arg1/arg2", trailing empties dropped
            const newIntegrationName = joinScheduleIntegration(process, args);

            // Update the integration in the schedule state
            const newIntegrations = schedule.integrations.map(
                (integration, mapIndex) => {
                    if (mapIndex === integrationIndex) {
                        return newIntegrationName;
                    }
                    return integration;
                }
            );

            onSetSchedule({
                ...schedule,
                integrations: [...newIntegrations],
            });

            // A new integration input that was just added, does not exist in state
        } else {
            onSetSchedule({
                ...schedule,
                integrations: [...schedule.integrations, "Select Integration"],
            });
        }
    };

    const addIntegrationInput = (indexToAdd: number) => {
        onSetIntegrationInputs(integrationInputs + 1);

        const newIntegrations = [...schedule.integrations];
        newIntegrations.splice(indexToAdd + 1, 0, "Select Integration");

        onSetSchedule({
            ...schedule,
            integrations: newIntegrations,
        });
    };

    const removeIntegrationInput = (indexToRemove: number) => {
        onSetIntegrationInputs(integrationInputs - 1);

        const newIntegrations = schedule.integrations.filter(
            (v, i) => i !== indexToRemove
        );

        onSetSchedule({
            ...schedule,
            integrations: newIntegrations,
        });
    };

    /** Drag-to-reorder integration rows. Splice the integrations array on drop
     * and push it back through onSetSchedule — the rest of the form re-renders
     * from schedule.integrations and the new order is what gets submitted. */
    const onIntegrationDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const from = result.source.index;
        const to = result.destination.index;
        if (from === to) return;
        const reordered = [...schedule.integrations];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        onSetSchedule({
            ...schedule,
            integrations: reordered,
        });
    };

    const switchScheduleType = (e: React.MouseEvent) => {
        e.preventDefault();
        const newIsRate = !isRate;
        setIsRate(newIsRate);

        const newTrigger = newIsRate ? "rate(1 days)" : defaultCron;
        onSetSchedule({
            ...schedule,
            trigger: newTrigger,
        });
    };

    // Cron component errors
    if (error) {
        console.log(error);
    }

    const nameShown =
        parent === "edit" ? schedule.name.replace(/_+/g, " ") : schedule.name;

    return (
        <Form>
            {/* Name */}
            <Row className={styles.inputRow}>
                <Col sm={2}>
                    <Label htmlFor="name">
                        <b>Name: </b>
                    </Label>
                </Col>
                <Col sm={9}>
                    <Input
                        id="name"
                        name="name"
                        value={nameShown}
                        onChange={scheduleChange}
                        valid={nameValid}
                        invalid={nameValid === undefined ? false : !nameValid}
                        minLength={3}
                        maxLength={50}
                    />
                    <FormFeedback>
                        Name may not be a duplicate. It must contain at least
                        three characters, and may only consists of digits,
                        letters, spaces, or hyphens (ex: Get Nightly Schedules
                        or Get-Nightly-Schedules)
                    </FormFeedback>
                </Col>
            </Row>
            <hr />

            {/* Enable / disable */}
            <Row className={styles.inputRow}>
                <Col sm={2}>
                    <Label htmlFor="enabled">
                        {" "}
                        <b>Enabled: </b>
                    </Label>
                </Col>
                <Col sm={9}>
                    <label className={styles.enableSwitch}>
                        <input
                            type="checkbox"
                            id="enabled"
                            name="enabled"
                            checked={schedule.enabled}
                            onChange={scheduleChange}
                        />
                        <span className={styles.slider} />
                    </label>
                </Col>
            </Row>
            <hr />

            {/* Cron expression builder or rate expression builder plus inputs */}

            {isRate && schedule.trigger.startsWith("rate") ? (
                <Row>
                    <Col sm={2}>
                        <b>Schedule: </b>
                    </Col>
                    <Col>
                        <RateSelector
                            schedule={schedule}
                            onSetSchedule={onSetSchedule}
                            isRateInvalid={isRateInvalid}
                            onSetIsRateInvalid={onSetIsRateInvalid}
                        />
                    </Col>
                </Row>
            ) : (
                <div className={styles.cronBox}>
                    <Row>
                        <Col sm={2}>
                            <b>Schedule: </b>
                        </Col>
                        <Col>
                            {/* Only render Cron if we have a trigger (show nothing while state 
                            is updating) */}
                            <Cron
                                humanizeValue={true}
                                value={cron}
                                setValue={onSetCron}
                                onError={setError}
                            />
                        </Col>
                    </Row>
                    <p>
                        {error
                            ? `Error: ${error.description}`
                            : cron &&
                              `Will run: ${cronstrue.toString(cron, {
                                  verbose: true,
                                  use24HourTimeFormat: true,
                              })}`}
                    </p>
                    <p style={{ fontSize: 12 }}>
                        Click a dropdown option to select or unselect it.
                        Multiple values can be chosen for some dropdowns. For
                        example, to run schedule every 15 minutes, select 0, 15,
                        30, and 45 from the minutes dropdown. Schedules to run
                        every minute are not allowed.
                    </p>
                </div>
            )}

            {/* Switch between rate/cron button */}
            <Row>
                <Button
                    id="switchBtn"
                    className={styles.switchBtn}
                    onClick={switchScheduleType}
                >
                    Switch Schedule Type
                </Button>
                <UncontrolledTooltip target="switchBtn">
                    {isRate
                        ? "Switch to cron expression - more granular control"
                        : "Switch to schedule repeated every x days - simpler"}
                </UncontrolledTooltip>
            </Row>

            <hr />

            {/* Integrations - DragDropContext wraps the rows so the user can
                reorder them. onIntegrationDragEnd splices schedule.integrations. */}
            <DragDropContext onDragEnd={onIntegrationDragEnd}>
                <Droppable droppableId="scheduler-integrations">
                    {(droppableProvided) => (
                        <div
                            className={styles.integrationsAreaBox}
                            ref={droppableProvided.innerRef}
                            {...droppableProvided.droppableProps}
                        >
                            {new Array(integrationInputs).fill(1).map((v, i) => (
                                <Draggable
                                    key={`integration-row-${i}`}
                                    draggableId={`integration-row-${i}`}
                                    index={i}
                                >
                                    {(draggableProvided, snapshot) => (
                                        <div
                                            ref={draggableProvided.innerRef}
                                            {...draggableProvided.draggableProps}
                                            style={{
                                                ...draggableProvided.draggableProps.style,
                                                background: snapshot.isDragging
                                                    ? "rgba(37, 99, 235, 0.08)"
                                                    : undefined,
                                            }}
                                        >
                                            <Row className={styles.inputRow}>
                                                {
                                                    <Col sm={2}>
                                                        <Label htmlFor={`integration ${i}`}>
                                                            {i === 0 && <b>Integrations: </b>}
                                                        </Label>
                                                    </Col>
                                                }
                                                <Col sm={9} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <span
                                                        {...draggableProvided.dragHandleProps}
                                                        title="Drag to reorder"
                                                        style={{
                                                            cursor: "grab",
                                                            color: "#94a3b8",
                                                            padding: "4px 6px",
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faGripVertical} />
                                                    </span>
                                                    <div style={{ flex: 1 }}>
                                                        <Input
                                                            type="select"
                                                            name={`integration| ${i}`}
                                                            value={
                                                                schedule.integrations[i]?.split(
                                                                    "/"
                                                                )[0] ?? ""
                                                            }
                                                            onChange={scheduleChange}
                                                        >
                                                            <option value="Select Integration" disabled>
                                                                &nbsp;&nbsp;Select Integration
                                                            </option>
                                                            {(() => {
                                                                const selectedProcess = schedule.integrations[i]?.split("/")[0];
                                                                const existsInCode = !selectedProcess
                                                                    || selectedProcess === "Select Integration"
                                                                    || integrations.some((int) => int.process === selectedProcess);
                                                                if (!existsInCode) {
                                                                    return (
                                                                        <option value={selectedProcess} disabled>
                                                                            &nbsp;&nbsp;{selectedProcess} *Delete - No longer exists
                                                                        </option>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                            {integrations.map((i, index, array) => (
                                                                <IntegrationOption
                                                                    key={i.name + index}
                                                                    integration={i}
                                                                    index={index}
                                                                    array={array}
                                                                />
                                                            ))}
                                                        </Input>
                                                        <IntegrationArguments
                                                            integrationIndex={i}
                                                            integrations={integrations}
                                                            schedule={schedule}
                                                            scheduleChange={scheduleChange}
                                                        />
                                                        <Button
                                                            onClick={() => addIntegrationInput(i)}
                                                            disabled={integrationInputs >= 25}
                                                            className={styles.addIntegrationBtn}
                                                        >
                                                            Add
                                                        </Button>
                                                        <span> </span>
                                                        <Button
                                                            onClick={() => removeIntegrationInput(i)}
                                                            disabled={integrationInputs <= 1}
                                                            className={styles.removeIntegrationBtn}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>

                                            {/* Arguments for a selected input if any */}
                                            <Row />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {droppableProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </Form>
    );
}
